import { useMemo, useState } from 'react';
import { Button } from '@mui/material';
import { Home, Mail, MessageCircle, RefreshCw } from 'lucide-react';

import { LabFacepalm, LabSad } from '@labradorsports/assets';
import { ButtonsBar, MailTo, StyledTextField } from '@labradorsports/components';
import { AllErrors, Exception } from '@labradorsports/utils';

import { useLogger } from '../../shared/index.js';
import { useSendDeveloperNoteMutation } from '../../store/index.js';

interface Props {
    error: Exception;
}

const ErrorDetails: FC<Props> = ({ error }) => {
    const logger = useLogger();
    const errorDetails = AllErrors[error?.code];
    const [showDeveloperNote, setShowDeveloperNote] = useState(false);
    const [developerNote, setDeveloperNote] = useState('');
    const [sendDeveloperNote] = useSendDeveloperNoteMutation();
    const [developerNoteSent, setDeveloperNoteSent] = useState(false);
    const [refreshing, setRefreshing] = useState(false);

    const ErrorSplash = useMemo(() => (Math.random() > 0.5 ? LabSad : LabFacepalm), []);

    const refresh = () => {
        setRefreshing(true);
        // Pause to allow more time for error logs to reach the server
        setTimeout(() => {
            if (APP) {
                window.location.href = 'index.html';
            } else {
                window.location.reload();
            }
        }, 1000);
    };

    const submitDeveloperNote = () => {
        setShowDeveloperNote(false);
        setDeveloperNoteSent(true);
        sendDeveloperNote({ note: developerNote, error });
        logger.exception(error);
    };

    return (
        <div className="error-details">
            <div className="p-3">
                <div className="text-center">
                    <ErrorSplash className="error-splash-image" />
                </div>
                {error ? (
                    <>
                        <p>
                            <b>{errorDetails ? errorDetails.message : 'Something went wrong.'}</b>
                        </p>
                        <p className="small">
                            {errorDetails?.extendedMessage ? (
                                <span>{errorDetails?.extendedMessage}</span>
                            ) : (
                                <span>
                                    If this keeps happening, please contact us at{' '}
                                    <MailTo email="support@labradorsports.com" />
                                </span>
                            )}
                        </p>
                        <p className="small text-muted">
                            Error code: {error.code}-{error.id}
                        </p>
                    </>
                ) : null}
                {showDeveloperNote ? (
                    <div className="dev-note-form">
                        <p>What were you doing when the error occurred?</p>
                        <div className="d-flex align-items-center">
                            <StyledTextField
                                label="Add Note"
                                value={developerNote}
                                onChange={(evt) => setDeveloperNote(evt.target.value)}
                                fullWidth
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={submitDeveloperNote}
                                disabled={refreshing}
                            >
                                <Mail className="button-icon" />
                                Send
                            </Button>
                        </div>
                    </div>
                ) : null}
                {developerNoteSent ? (
                    <p>Thank you for your note. We will review it and get back to you as soon as possible.</p>
                ) : null}
            </div>
            <ButtonsBar>
                {developerNoteSent ? null : (
                    <Button onClick={() => setShowDeveloperNote(true)}>
                        <MessageCircle className="button-icon" />
                        Send Developer Note
                    </Button>
                )}
                <Button variant="contained" color="primary" onClick={refresh} disabled={refreshing}>
                    <RefreshCw className="button-icon" />
                    Refresh
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={() => (window.location.href = '/')}
                    disabled={refreshing}
                >
                    <Home className="button-icon" />
                    Home
                </Button>
            </ButtonsBar>
        </div>
    );
};

export default ErrorDetails;
